import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Iconify from "../../../../components/iconify";
import { useGetSitesQuery } from "../../../../features/sites/sitesApiSlice";
import {
  useCreateTrainingMutation,
  useDeleteTrainingMutation,
  useGetTrainingsQuery,
} from "../../../../features/trainings/trainingsApiSlice";
import useAuth from "../../../../hooks/useAuth";
import {
  ATTENDANCE_LAST_SITE_KEY,
  EMPTY_ARRAY,
  toDateInputValue,
  toDateRangeFromPreset,
  toId,
  toLocalDateLabel,
  toLocalTimeLabel,
  toStartAtIso,
  toTimeInputValue,
} from "./attendanceUtils";

const DATE_FILTERS = [
  { value: "today", label: "Heute" },
  { value: "yesterday", label: "Gestern" },
  { value: "custom", label: "Benutzerdefiniert" },
];

const INITIAL_NOTICE = {
  open: false,
  message: "",
  severity: "success",
};

const statusColor = (status) => (status === "closed" ? "default" : "success");

export default function AttendanceLandingView() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isUser, isEditor, isAdmin, isSuperAdmin } = useAuth();
  const canManageAttendance = isUser || isEditor || isAdmin || isSuperAdmin;

  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [datePreset, setDatePreset] = useState("today");
  const [customDate, setCustomDate] = useState(toDateInputValue());
  const [searchTerm, setSearchTerm] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [notice, setNotice] = useState(INITIAL_NOTICE);
  const [deletingId, setDeletingId] = useState("");
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    training: null,
  });

  const [createSiteId, setCreateSiteId] = useState("");
  const [createDate, setCreateDate] = useState(toDateInputValue());
  const [createTime, setCreateTime] = useState(toTimeInputValue());
  const [createType, setCreateType] = useState("");
  const [createNotes, setCreateNotes] = useState("");

  const { data: sitesResponse = EMPTY_ARRAY } = useGetSitesQuery();
  const sites = Array.isArray(sitesResponse) ? sitesResponse : EMPTY_ARRAY;

  useEffect(() => {
    if (!sites.length) {
      setSelectedSiteId("");
      return;
    }

    setSelectedSiteId((current) => {
      if (current && sites.some((site) => toId(site) === current)) {
        return current;
      }

      const storedSiteId =
        typeof window !== "undefined"
          ? window.localStorage.getItem(ATTENDANCE_LAST_SITE_KEY)
          : "";

      if (storedSiteId && sites.some((site) => toId(site) === storedSiteId)) {
        return storedSiteId;
      }

      return toId(sites[0]);
    });
  }, [sites]);

  useEffect(() => {
    if (!selectedSiteId || typeof window === "undefined") return;
    window.localStorage.setItem(ATTENDANCE_LAST_SITE_KEY, selectedSiteId);
  }, [selectedSiteId]);

  const dateRange = useMemo(
    () => toDateRangeFromPreset({ preset: datePreset, customDate }),
    [datePreset, customDate],
  );

  const trainingsQuery = useMemo(
    () => ({
      siteId: selectedSiteId || undefined,
      from: dateRange.from,
      to: dateRange.to,
    }),
    [selectedSiteId, dateRange.from, dateRange.to],
  );

  const {
    data: trainingsResponse = EMPTY_ARRAY,
    isFetching: trainingsLoading,
    isError: trainingsError,
    refetch: refetchTrainings,
  } = useGetTrainingsQuery(trainingsQuery, {
    skip: !selectedSiteId,
  });
  const trainings = Array.isArray(trainingsResponse)
    ? trainingsResponse
    : EMPTY_ARRAY;

  const [createTraining, { isLoading: isCreatingTraining }] =
    useCreateTrainingMutation();
  const [deleteTraining, { isLoading: isDeletingTraining }] =
    useDeleteTrainingMutation();

  const filteredTrainings = useMemo(() => {
    if (!searchTerm.trim()) return trainings;
    const query = searchTerm.trim().toLowerCase();
    return trainings.filter((training) =>
      [training?.title, training?.trainingType, training?.site?.siteName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [searchTerm, trainings]);

  const selectedSite = useMemo(
    () => sites.find((site) => toId(site) === selectedSiteId) || null,
    [sites, selectedSiteId],
  );

  const handleOpenCreate = () => {
    if (!sites.length) {
      setNotice({
        open: true,
        message:
          "Kein Standort verfügbar. Bitte zuerst einen Standort erstellen oder zuweisen.",
        severity: "warning",
      });
      return;
    }

    setCreateSiteId(selectedSiteId || toId(sites[0]));
    setCreateDate(toDateInputValue());
    setCreateTime(toTimeInputValue());
    setCreateType("");
    setCreateNotes("");
    setCreateOpen(true);
  };

  const handleCreateTraining = async () => {
    const startAt = toStartAtIso({
      dateValue: createDate,
      timeValue: createTime,
    });

    if (!createSiteId || !startAt) {
      setNotice({
        open: true,
        message: "Bitte Standort, Datum und Startzeit angeben.",
        severity: "warning",
      });
      return;
    }

    try {
      const response = await createTraining({
        siteId: createSiteId,
        startAt,
        trainingType: createType,
        notes: createNotes,
      }).unwrap();

      const createdId = response?.training?._id;
      if (!createdId) {
        throw new Error("Training-ID fehlt in der Antwort.");
      }

      setSelectedSiteId(createSiteId);
      setCreateOpen(false);
      navigate(`/dashboard/members/attendance/${createdId}`);
    } catch (error) {
      setNotice({
        open: true,
        message:
          error?.data?.message || "Training konnte nicht erstellt werden.",
        severity: "error",
      });
    }
  };

  const handleRequestDelete = (event, training) => {
    event?.preventDefault();
    event?.stopPropagation();

    const trainingId = training?._id;
    if (!trainingId) return;

    setConfirmDelete({ open: true, training });
  };

  const handleConfirmDelete = async () => {
    const trainingId = confirmDelete.training?._id;
    if (!trainingId) return;

    setDeletingId(trainingId);

    try {
      await deleteTraining(trainingId).unwrap();
      setNotice({
        open: true,
        message: "Training wurde gelöscht.",
        severity: "success",
      });
      setConfirmDelete({ open: false, training: null });
    } catch (error) {
      setNotice({
        open: true,
        message:
          error?.data?.message || "Training konnte nicht gelöscht werden.",
        severity: "error",
      });
    } finally {
      setDeletingId("");
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{ px: { xs: 1, sm: 2 }, pb: { xs: 3, sm: 4 }, pt: { xs: 1, sm: 2 } }}
    >
      <Stack spacing={2.25}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            borderColor: alpha(theme.palette.primary.main, 0.25),
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.light,
              0.25,
            )} 0%, ${alpha(theme.palette.info.light, 0.26)} 100%)`,
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Iconify icon="mdi:clipboard-check-outline" width={22} />
                <Typography variant="h4">Anwesenheit</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Starte schnell ein Training und trage Mitglieder mit einem Tipp
                ein.
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={
                  <Iconify icon="mdi:play-circle-outline" width={22} />
                }
                onClick={handleOpenCreate}
                disabled={!canManageAttendance}
                sx={{ minHeight: 46, alignSelf: "flex-start" }}
              >
                Training starten
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <ToggleButtonGroup
                color="primary"
                exclusive
                value={datePreset}
                onChange={(_event, nextValue) => {
                  if (nextValue) setDatePreset(nextValue);
                }}
                fullWidth
                sx={{ "& .MuiToggleButton-root": { minHeight: 42 } }}
              >
                {DATE_FILTERS.map((filter) => (
                  <ToggleButton key={filter.value} value={filter.value}>
                    {filter.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              {datePreset === "custom" && (
                <TextField
                  type="date"
                  label="Datum"
                  value={customDate}
                  onChange={(event) => setCustomDate(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}

              <TextField
                select
                label="Standort"
                value={selectedSiteId}
                onChange={(event) => setSelectedSiteId(event.target.value)}
                fullWidth
              >
                {sites.map((site) => (
                  <MenuItem key={toId(site)} value={toId(site)}>
                    {site?.siteName || "Standort"}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Trainings suchen"
                placeholder="Titel oder Trainingstyp"
                fullWidth
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <Iconify
                      icon="mdi:magnify"
                      width={20}
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                  ),
                }}
              />
            </Stack>
          </CardContent>
        </Card>

        <Stack spacing={1.25}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle2" color="text.secondary">
              {selectedSite?.siteName || "Ausgewählter Standort"} •{" "}
              {toLocalDateLabel(dateRange.selectedDate)}
            </Typography>
            <Button size="small" onClick={refetchTrainings}>
              Aktualisieren
            </Button>
          </Stack>

          {trainingsLoading &&
            Array.from({ length: 3 }).map((_, index) => (
              <Card key={`training-skeleton-${index}`} variant="outlined">
                <CardContent>
                  <Skeleton variant="text" width="70%" />
                  <Skeleton variant="text" width="45%" />
                  <Skeleton variant="rounded" width={110} height={28} />
                </CardContent>
              </Card>
            ))}

          {!trainingsLoading && trainingsError && (
            <Alert severity="error">
              Trainings konnten für dieses Datum/diesen Standort nicht geladen
              werden.
            </Alert>
          )}

          {!trainingsLoading &&
            !trainingsError &&
            filteredTrainings.length === 0 && (
              <Card
                variant="outlined"
                sx={{ textAlign: "center", borderRadius: 3 }}
              >
                <CardContent sx={{ py: 4 }}>
                  <Stack spacing={1.25} alignItems="center">
                    <Iconify
                      icon="mdi:clipboard-outline"
                      width={46}
                      sx={{ color: "text.disabled" }}
                    />
                    <Typography variant="h6">Noch keine Trainings</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Starte jetzt ein Training und beginne direkt mit der
                      Anwesenheit.
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<Iconify icon="mdi:plus" width={18} />}
                      onClick={handleOpenCreate}
                      sx={{ minHeight: 44 }}
                    >
                      Training erstellen
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            )}

          {!trainingsLoading &&
            filteredTrainings.map((training) => (
              <Card
                key={training?._id}
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  transition: "transform 0.16s ease, box-shadow 0.16s ease",
                  "&:hover": { transform: "translateY(-1px)", boxShadow: 4 },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(`${training?._id}`)}
                  sx={{ minHeight: 92 }}
                >
                  <CardContent sx={{ py: 1.75 }}>
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          {`Training - ${
                            training?.site?.siteName || "Standort"
                          }`}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={
                              training?.status === "closed"
                                ? "Geschlossen"
                                : "Offen"
                            }
                            color={statusColor(training?.status)}
                            size="small"
                          />
                          <Button
                            size="small"
                            color="error"
                            variant="contained"
                            startIcon={
                              <Iconify
                                icon="mdi:trash-can-outline"
                                width={18}
                              />
                            }
                            onClick={(event) =>
                              handleRequestDelete(event, training)
                            }
                            onMouseDown={(event) => event.stopPropagation()}
                            disabled={
                              !canManageAttendance ||
                              isDeletingTraining ||
                              deletingId === training?._id
                            }
                            sx={{ minWidth: 0 }}
                          >
                            Löschen
                          </Button>
                        </Stack>
                      </Stack>

                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {toLocalDateLabel(training?.startAt)} ·{" "}
                          {toLocalTimeLabel(training?.startAt)}
                        </Typography>
                        <Divider orientation="vertical" flexItem />
                        <Chip
                          icon={
                            <Iconify
                              icon="mdi:check-circle-outline"
                              width={16}
                            />
                          }
                          size="small"
                          label={`${training?.presentCount || 0} anwesend`}
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
        </Stack>
      </Stack>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialog-container": {
            alignItems: isMobile ? "flex-end" : "center",
          },
        }}
        PaperProps={{
          sx: isMobile
            ? {
                m: 0,
                width: "100%",
                borderTopLeftRadius: 18,
                borderTopRightRadius: 18,
              }
            : { borderRadius: 3 },
        }}
      >
        <DialogTitle>Training erstellen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              label="Standort *"
              value={createSiteId}
              onChange={(event) => setCreateSiteId(event.target.value)}
              fullWidth
            >
              {sites.map((site) => (
                <MenuItem key={toId(site)} value={toId(site)}>
                  {site?.siteName || "Standort"}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction="row" spacing={1.5}>
              <TextField
                type="date"
                label="Datum *"
                value={createDate}
                onChange={(event) => setCreateDate(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="time"
                label="Startzeit *"
                value={createTime}
                onChange={(event) => setCreateTime(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <TextField
              label="Trainingstyp (optional)"
              value={createType}
              onChange={(event) => setCreateType(event.target.value)}
              fullWidth
            />

            <TextField
              label="Notizen (optional)"
              value={createNotes}
              onChange={(event) => setCreateNotes(event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCreateTraining}
            disabled={isCreatingTraining}
            sx={{ minHeight: 44 }}
          >
            {isCreatingTraining ? "Wird erstellt..." : "Erstellen"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, training: null })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Training löschen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Möchtest du dieses Training wirklich löschen? Diese Aktion kann nicht
            rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete({ open: false, training: null })}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={isDeletingTraining}
          >
            {isDeletingTraining ? "Wird gelöscht..." : "Löschen"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notice.open}
        autoHideDuration={2600}
        onClose={() => setNotice(INITIAL_NOTICE)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={notice.severity}
          variant="filled"
          onClose={() => setNotice(INITIAL_NOTICE)}
        >
          {notice.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
